import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, RefreshCcw, Loader2, CalendarClock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RENEWAL_STATUS, RENEWAL_PRIORITY, RENEWAL_BUCKETS, OPEN_RENEWAL_STATUSES, daysLeftLabel, translateReason } from "@/lib/renewals";
import { typeLabel, fmtDate } from "@/lib/contracts";
import { cn } from "@/lib/utils";
import { useLang } from "@/i18n";
import api from "@/lib/api";

const KPI = ({ label, value, accent, testid }) => (
  <div className="rounded-2xl border border-border bg-surface p-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
    <p className={cn("text-2xl font-semibold mt-1.5", accent)}>{value}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const { tf } = useLang();
  const m = RENEWAL_STATUS[status] || RENEWAL_STATUS.to_qualify;
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium", m.cls)}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: m.dot }} />
      {tf(m.label)}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const { tf } = useLang();
  const m = RENEWAL_PRIORITY[priority?.level] || RENEWAL_PRIORITY.low;
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold", m.cls)}
      title={(priority?.reasons || []).join(" · ")}>
      {tf(m.label)}
    </span>
  );
};

const RenewalRow = ({ c, onClick, index }) => {
  const { tf, lang } = useLang();
  return (
  <motion.button
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.25, delay: Math.min(index * 0.04, 0.3) }}
    onClick={onClick} data-testid={`renewal-row-${c.id}`}
    className="w-full text-left rounded-2xl border border-border bg-surface p-4 sm:p-5 hover:border-foreground/25 hover:bg-secondary/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2.5 flex-wrap">
          <p className="text-sm font-semibold truncate">{c.player_name || c.contract_title}</p>
          <PriorityBadge priority={c.priority} />
          <StatusBadge status={c.status} />
        </div>
        <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5 flex-wrap">
          <span>{c.contract_title}</span>
          <span className="opacity-40">·</span>
          <span>{typeLabel(c.contract_type)}</span>
          {c.counterparty && <><span className="opacity-40">·</span><span>{c.counterparty}</span></>}
        </p>
        {c.priority?.reasons?.[0] && (
          <p className="text-[11px] mt-1.5 text-muted-foreground/90 flex items-center gap-1.5">
            <span className={cn("h-1 w-1 rounded-full shrink-0",
              c.priority.level === "critical" ? "bg-red-400" : c.priority.level === "high" ? "bg-orange-400" : "bg-blue-400")} />
            {translateReason(c.priority.reasons[0], lang)}
          </p>
        )}
      </div>
      <div className="flex items-center gap-4 shrink-0 text-right">
        <div>
          <p className={cn("text-xs font-semibold tabular-nums",
            c.days_left != null && c.days_left <= 60 ? "text-red-400" : c.days_left != null && c.days_left <= 90 ? "text-amber-600 dark:text-amber-400" : "text-foreground")}>
            {daysLeftLabel(c.days_left, lang)}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{tf("Fin :")} {fmtDate(c.contract_end_date)}</p>
        </div>
      </div>
    </div>
  </motion.button>
  );
};

export const RenewalsBoard = () => {
  const navigate = useNavigate();
  const { tf } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [bucketFilter, setBucketFilter] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await api.get("/renewals");
      setData(r.data);
    } catch (e) { console.error("renewals load", e); setError(true); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let rows = data?.cases || [];
    if (statusFilter === "open") rows = rows.filter((c) => OPEN_RENEWAL_STATUSES.includes(c.status));
    else if (statusFilter !== "all") rows = rows.filter((c) => c.status === statusFilter);
    if (bucketFilter) rows = rows.filter((c) => c.bucket === bucketFilter);
    if (q.trim()) {
      const ql = q.trim().toLowerCase();
      rows = rows.filter((c) => [c.player_name, c.contract_title, c.counterparty].some((v) => (v || "").toLowerCase().includes(ql)));
    }
    return rows;
  }, [data, statusFilter, bucketFilter, q]);

  const k = data?.kpis || {};
  const buckets = k.buckets || {};

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-500/20 bg-red-500/[0.04] p-10 text-center" data-testid="renewals-error">
        <p className="text-sm font-medium">{tf("Impossible de charger les renouvellements")}</p>
        <p className="text-xs text-muted-foreground mt-1">{tf("Vérifiez votre connexion puis réessayez.")}</p>
        <Button size="sm" variant="outline" className="mt-4" onClick={load} data-testid="renewals-retry-btn">
          <RefreshCcw className="h-4 w-4 mr-1.5" /> {tf("Réessayer")}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="renewals-board">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI label={tf("Dossiers ouverts")} value={k.open_total ?? "—"} testid="renewal-kpi-open" />
        <KPI label={tf("Critiques")} value={k.critical ?? "—"} accent="text-red-400" testid="renewal-kpi-critical" />
        <KPI label={tf("Échéance ≤ 90 j")} value={k.expiring_90 ?? "—"} accent="text-amber-600 dark:text-amber-400" testid="renewal-kpi-90" />
        <KPI label={tf("Signés")} value={k.signed ?? "—"} accent="text-green-500" testid="renewal-kpi-signed" />
      </div>

      <div className="rounded-2xl border border-border bg-surface p-4" data-testid="renewal-roadmap">
        <div className="flex items-center gap-2 mb-3">
          <CalendarClock className="h-4 w-4 text-muted-foreground" />
          <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">{tf("Feuille de route des échéances")}</p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {RENEWAL_BUCKETS.map((b) => (
            <button key={b.id} onClick={() => setBucketFilter(bucketFilter === b.id ? null : b.id)}
              data-testid={`renewal-bucket-${b.id}`}
              className={cn("rounded-xl border p-2.5 text-center transition-colors",
                bucketFilter === b.id ? "border-white/25 bg-secondary/50" : "border-border hover:border-foreground/25")}>
              <p className={cn("text-lg font-semibold tabular-nums", b.tone)}>{buckets[b.id] ?? 0}</p>
              <p className="text-[10px] text-muted-foreground mt-0.5">{tf(b.label)}</p>
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder={tf("Rechercher un athlète, un contrat, un club…")} value={q}
            onChange={(e) => setQ(e.target.value)} data-testid="renewals-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-[230px]" data-testid="renewals-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">{tf("Dossiers ouverts")}</SelectItem>
            <SelectItem value="all">{tf("Tous les dossiers")}</SelectItem>
            {Object.entries(RENEWAL_STATUS).map(([id, m]) => <SelectItem key={id} value={id}>{tf(m.label)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-14 text-center" data-testid="renewals-empty">
          <RefreshCcw className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-sm font-medium">
            {(data?.cases || []).length === 0 ? tf("Aucun renouvellement détecté") : tf("Aucun dossier ne correspond à vos filtres")}
          </p>
          <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            {tf("Les contrats signés ou actifs arrivant à échéance dans les 365 prochains jours sont détectés automatiquement et apparaissent ici.")}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((c, i) => <RenewalRow key={c.id} c={c} index={i} onClick={() => navigate(`/contracts/renewals/${c.id}`)} />)}
        </div>
      )}
    </div>
  );
};
